import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../shell/AuthProvider";
import { loxleyApi, type LoxleyStatus } from "../lib/loxleyApi";

export type LoxleyRecordKind =
  | "company" | "contact" | "lead" | "opportunity" | "product"
  | "quote" | "order" | "invoice" | "agreement" | "ticket";

/** A record the user can add to a Loxley chat. The server reads it by kind and id; the label is for the chip. */
export type LoxleyRecord = { kind: LoxleyRecordKind; id: string; label: string };

/** The server reads at most this many added records per request (engine.MAX_PINNED). */
export const MAX_PINNED = 3;

type LoxleyContextValue = {
  orgId: string | null;
  status: LoxleyStatus | null;
  /** The add-on is on and this user holds a seat. */
  available: boolean;
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => void;
  /** The record the current screen is showing, offered as "Add to chat". */
  offered: LoxleyRecord | null;
  offer: (record: LoxleyRecord | null) => void;
  /** Records added to this chat; they stay until removed or a new chat starts. */
  pinned: LoxleyRecord[];
  pin: (record: LoxleyRecord) => void;
  unpin: (id: string) => void;
  clearPinned: () => void;
};

const LoxleyContext = createContext<LoxleyContextValue | null>(null);

// Hiding Loxley is a per-browser convenience, so it lives in localStorage.
// Storage can be missing (private window, blocked site data): fail visible.
function hiddenKey(userId: string): string {
  return `saleslox.loxley.hidden.${userId}`;
}

function readHidden(userId: string): boolean {
  try {
    return window.localStorage.getItem(hiddenKey(userId)) === "1";
  } catch {
    return false;
  }
}

function writeHidden(userId: string, hidden: boolean): void {
  try {
    if (hidden) window.localStorage.setItem(hiddenKey(userId), "1");
    else window.localStorage.removeItem(hiddenKey(userId));
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

export function LoxleyProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const userId = session?.userId ?? null;
  const [status, setStatus] = useState<LoxleyStatus | null>(null);
  const [hidden, setHiddenState] = useState(false);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [offered, setOffered] = useState<LoxleyRecord | null>(null);
  const [pinned, setPinned] = useState<LoxleyRecord[]>([]);

  useEffect(() => {
    setHiddenState(userId ? readHidden(userId) : false);
  }, [userId]);

  useEffect(() => {
    if (!orgId || session?.isPlatformStaff) {
      setStatus(null);
      return;
    }
    let live = true;
    loxleyApi
      .status(orgId)
      .then((value) => { if (live) setStatus(value); })
      .catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [orgId, session?.isPlatformStaff, tick]);

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next);
    if (next) setOpen(false);
    if (userId) writeHidden(userId, next);
  }, [userId]);

  const refresh = useCallback(() => setTick((value) => value + 1), []);

  const pin = useCallback((record: LoxleyRecord) => {
    setPinned((current) => (
      current.some((item) => item.id === record.id) ? current : [...current, record].slice(-MAX_PINNED)
    ));
  }, []);
  const unpin = useCallback((id: string) => setPinned((current) => current.filter((item) => item.id !== id)), []);
  const clearPinned = useCallback(() => setPinned([]), []);

  // A new org or user never inherits the last one's records. Only on a change, not on mount: a
  // screen's offer effect runs before this provider's and must survive the first render.
  const owner = useRef(`${orgId}:${userId}`);
  useEffect(() => {
    const next = `${orgId}:${userId}`;
    if (owner.current === next) return;
    owner.current = next;
    setPinned([]);
    setOffered(null);
  }, [orgId, userId]);

  const value = useMemo<LoxleyContextValue>(() => ({
    orgId,
    status,
    available: Boolean(status?.enabled && status.has_seat),
    hidden,
    setHidden,
    open,
    setOpen,
    refresh,
    offered,
    offer: setOffered,
    pinned,
    pin,
    unpin,
    clearPinned,
  }), [orgId, status, hidden, setHidden, open, refresh, offered, pinned, pin, unpin, clearPinned]);

  return <LoxleyContext.Provider value={value}>{children}</LoxleyContext.Provider>;
}

export function useOptionalLoxley(): LoxleyContextValue | null {
  return useContext(LoxleyContext);
}

/**
 * Offer the record this screen shows to Loxley ("Add Derek Marino to chat"). Pass null when nothing
 * is shown. One call per screen: the screen decides which record wins (an open contact over its company).
 */
export function useLoxleyOffer(record: LoxleyRecord | null): void {
  const offer = useContext(LoxleyContext)?.offer;
  const kind = record?.kind;
  const id = record?.id;
  const label = record?.label;
  useEffect(() => {
    if (!offer) return;
    if (!kind || !id || !label) {
      offer(null);
      return;
    }
    const mine: LoxleyRecord = { kind, id, label };
    offer(mine);
    return () => offer(null);
  }, [offer, kind, id, label]);
}
